import React, { MouseEventHandler } from 'react';
import { ImageOptionalLink } from '../../../../commonTypes/links';
import { applyComponentTheme } from '../../../../theme';
import { Grid } from '../../../Grid';
import GridContainer from '../../../GridContainer';
import NavDesktop from '../../NavDesktop';
import {
  PrimaryNav,
  SecondaryNav,
  TertiaryNavItem,
  TertiaryNavItemWithIcon,
} from '../../types';
import { LanguageToggleProps } from '../LanguageToggle';
import { Logo, MegaMenuContainer, StyledGridItem, StyledNavMobile } from './styles';

export type MegaMenuProps = {
  logoLink: ImageOptionalLink;
  primaryNav?: PrimaryNav;
  secondaryNav?: SecondaryNav;
  mobileMenuOpen: boolean;
  toggleMobileMenuOpen: () => void;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  tertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  secondTertiaryNavItem?: TertiaryNavItem;
  tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[];
  languageToggle?: LanguageToggleProps;
  toggleMenuCallback?: (toggled: boolean) => void;
  mobileMenuOnOpen?: MouseEventHandler;
  mobileMenuOnClose?: MouseEventHandler;
  activeNavItemIndex: number | null;
  setActiveNavItemIndex: React.Dispatch<React.SetStateAction<number | null>>;
  resetNav: () => void;
  isSticky?: boolean;
  mobileHeaderText?: string;
  fontColourVariant?: boolean;
  forceHamburgerMenuIcon?: boolean;
  showTertiaryIconsMobile?: boolean;
  usePrimaryNavFourColumnLayout?: boolean;
  showArrowsOnPrimaryNav?: boolean;
  phoneIconLink?: ImageOptionalLink;
  enableKeyboardNavigation?: boolean;
};

const MegaMenu: React.FC<MegaMenuProps> = ({
  logoLink,
  primaryNav,
  secondaryNav,
  mobileMenuOpen,
  toggleMobileMenuOpen,
  tertiaryNavItem,
  secondTertiaryNavItem,
  tertiaryNavItems,
  languageToggle,
  activeNavItemIndex,
  setActiveNavItemIndex,
  mobileMenuOnOpen,
  mobileMenuOnClose,
  resetNav,
  isSticky,
  mobileHeaderText,
  fontColourVariant,
  forceHamburgerMenuIcon = false,
  showTertiaryIconsMobile = false,
  usePrimaryNavFourColumnLayout = false,
  showArrowsOnPrimaryNav = false,
  phoneIconLink,
  enableKeyboardNavigation,
}) => {
  return (
    <MegaMenuContainer aria-label="main-nav" isSticky={isSticky ?? false}>
      <GridContainer>
        <Grid alignLeft>
          <StyledGridItem>
            <Logo logoLink={logoLink} background="dark" shouldEagerLoad={true} />
            {/* 'Mobile' nav is the burger-style nav, active for mobile and portrait tablet breakpoints. */}
            <StyledNavMobile
              primaryNav={primaryNav}
              secondaryNav={secondaryNav}
              tertiaryNavItem={tertiaryNavItem}
              secondTertiaryNavItem={secondTertiaryNavItem}
              tertiaryNavItems={tertiaryNavItems}
              languageToggle={languageToggle}
              menuOpen={mobileMenuOpen}
              toggleMenuOpen={toggleMobileMenuOpen}
              activeItemIndex={activeNavItemIndex}
              setActiveItemIndex={setActiveNavItemIndex}
              onOpen={mobileMenuOnOpen}
              onClose={mobileMenuOnClose}
              resetNav={resetNav}
              headerText={mobileHeaderText}
              fontColourVariant={!!fontColourVariant}
              forceHamburgerMenuIcon={forceHamburgerMenuIcon}
              showTertiaryIcons={showTertiaryIconsMobile}
              phoneIconLink={phoneIconLink}
            />
            {/* 'Desktop' nav is the inline nav, active for desktop and landscape tablet breakpoints. */}
            <NavDesktop
              primaryNav={primaryNav}
              useFourColumnLayout={usePrimaryNavFourColumnLayout}
              showArrowsOnPrimaryNav={showArrowsOnPrimaryNav}
              activeItemIndex={activeNavItemIndex}
              setActiveItemIndex={setActiveNavItemIndex}
              resetNav={resetNav}
              enableKeyboardNavigation={enableKeyboardNavigation}
            />
          </StyledGridItem>
        </Grid>
      </GridContainer>
    </MegaMenuContainer>
  );
};

export default applyComponentTheme(MegaMenu, 'MegaMenu');
